import './App.less';
import {Provider as InversifyProvider} from 'inversify-react';
import {Provider, useDispatch, useSelector} from 'react-redux'
import {store} from './redux/store';
import RoutingContainer, {RoutingPaths} from "./pages/routing/routingContainer";
import React, {useEffect, useLayoutEffect, useState} from "react";
import {BrowserRouter, useLocation} from "react-router-dom";
import SiderMenu from './components/siderMenu/siderMenu';
import HeaderContainer from './components/header/headerContainer';
import FooterContainer from './components/footer/footerContainer';
import {CrudBreadcrumb, CrudLayout} from 'digimuth-components/dist';
import {Button, ConfigProvider, message} from "antd";
import {Auth0Provider, useAuth0} from "@auth0/auth0-react";
import {setCurrentUser} from "./redux/applicationUserSlice";
import {getCurrentUser} from "./redux/applicationUserSlice";
import useCurrentUserRoles from "./hooks/useCurrentUserRoles";
import plPL from "antd/locale/pl_PL";
import {ConfigProviderProps} from 'antd/es/config-provider';
import { ApplicationUserApi } from './api';
import dayjs from 'dayjs';
import 'dayjs/locale/pl';
import {useScannerListener} from "./hooks/useScannerListener";
import {useServiceWorker} from "./hooks/useServiceWorker";
import DevScannerSimulator from "./components/devScannerSimulator/devScannerSimulator";
import RouteContextBanner from "./components/routeContextBanner/routeContextBanner";
import {bindApiService, getApiServiceOrNull, serviceContainer} from "./services/serviceContainer";
import QrCameraScannerFab from "./components/qrCameraScannerFab/qrCameraScannerFab";
import {
    isQrCameraScannerEnabledForUser,
    QR_CAMERA_SCANNER_PREFERENCE_CHANGED_EVENT
} from "./helpers/qrScannerPreferences";
import {getAuth0ScopesForFrontend} from "./helpers/applicationUserHelper";

dayjs.locale('pl');
declare const BrowserPrint: any;

function App() {

    const { showReload, reloadPage } = useServiceWorker();

    useEffect(() => {
        if (showReload) {
            message.info(
                <div>
                    Nowa wersja aplikacji jest dostępna.
                    <Button type={"primary"} style={{marginLeft:10}} onClick={reloadPage}>Odśwież</Button>
                </div>,
                0
            );
        }
    }, [showReload, reloadPage]);

    return (
        /* // @ts-ignore */
        <Auth0Provider
            domain={process.env.REACT_APP_AUTH0_DOMAIN!}
            clientId={process.env.REACT_APP_AUTH0_CLIENT_ID!}
            useRefreshTokens={true}
            cacheLocation={"localstorage"}
            onRedirectCallback={(appState) => {
                const returnTo = appState?.returnTo || "/";
                const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;

                if (currentPath !== returnTo) {
                    window.history.replaceState({}, document.title, returnTo);
                }
            }}
            authorizationParams={{
                redirect_uri: window.location.origin,
                audience: "https://localhost:5001",
                scope: getAuth0ScopesForFrontend()
            }}
        >
            <Provider store={store}>
                <AppInternal/>
            </Provider>
        </Auth0Provider>
    )
}

function AppInternal() {

    const auth = useAuth0();
    const dispatch = useDispatch();

    useEffect(() => {
        let isCancelled = false;

        if (!auth.isLoading && !auth.isAuthenticated) {
            localStorage.removeItem("cached_token");
            localStorage.removeItem("currentUser");
            dispatch(setCurrentUser(null));
        }

        if (!auth.isLoading && auth.isAuthenticated) {
            auth.getAccessTokenSilently().then(async t => {
                if (isCancelled) {
                    return;
                }

                bindApiService(t);
                localStorage.setItem("cached_token", t);

                const apiService = getApiServiceOrNull();
                if (!apiService) {
                    return;
                }
                const response = await apiService.getApi(ApplicationUserApi).apiApplicationUserGet();

                if (isCancelled) {
                    return;
                }

                dispatch(setCurrentUser(response.data));

                try {
                    // @ts-ignore
                    const config = await new Promise((resolve, reject) => {
                        BrowserPrint.getApplicationConfiguration(
                            (cfg: any) => resolve(cfg),
                            (err: any) => reject(err)
                        );
                    });
                    console.log("BrowserPrint client config:", config);
                    
                } catch (error) {
                    console.warn("BrowserPrint native client not available", error);
                }
                
                localStorage.setItem("currentUser", JSON.stringify(response.data));
            }).catch(error => {
                console.warn("Failed to initialize authenticated session", error);
                localStorage.removeItem("cached_token");
                localStorage.removeItem("currentUser");
                dispatch(setCurrentUser(null));
            });
        }

        return () => {
            isCancelled = true;
        };
    }, [auth.isLoading, auth.isAuthenticated, auth.getAccessTokenSilently, dispatch]);

    return <InversifyProvider container={serviceContainer}>
        <BrowserRouter>
            <Layout/>
        </BrowserRouter>
    </InversifyProvider>
}

function ScrollToTopOnRouteChange() {
    const {pathname, search} = useLocation();

    useLayoutEffect(() => {
        window.scrollTo({top: 0, left: 0, behavior: "auto"});
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;

        const layoutContent = document.querySelector(".ant-layout-content");
        if (layoutContent instanceof HTMLElement) {
            layoutContent.scrollTo({top: 0, left: 0, behavior: "auto"});
        }
    }, [pathname, search]);

    return null;
}

const CrudDefaultTheme = {
    "token": {
        "colorPrimary": "#15539e",
        "colorPrimaryHover": "#1d63b8",
        "colorPrimaryActive": "#123f79",
        "colorInfo": "#15539e",
        "colorLink": "#15539e",
        "colorSuccess": "#5b7199",
        "colorError": "#b04d5f",
        "colorWarning": "#a28f63",
        "wireframe": false,
        "borderRadius": 12,
        "sizeStep": 5,
        "sizeUnit": 4,
        "fontSize": 14,
        "fontFamily": "'Montserrat Variable', 'Montserrat', 'Segoe UI', sans-serif",
        "colorBgBase": "#fdfefe",
        "colorBgLayout": "#edf2f8",
        "colorTextBase": "#1c2430",
        "colorBorder": "#d7e1ee",
        "colorTextSecondary": "#5b7199",
        "controlHeight": 40,
        "controlHeightSM": 28
    },
    "components": {
        "Layout": {
            "bodyBg": "#edf2f8",
            "headerBg": "#f7fafe",
            "siderBg": "#0f2747",
            "triggerBg": "#123f79",
            "triggerColor": "#f5f8fc"
        },
        "Menu": {
            "darkItemBg": "#0f2747",
            "darkSubMenuItemBg": "#102b4e",
            "darkItemHoverBg": "#153a68",
            "darkItemSelectedBg": "#15539e",
            "darkItemSelectedColor": "#ffffff",
            "darkItemColor": "rgba(245, 248, 252, 0.82)",
            "darkGroupTitleColor": "rgba(245, 248, 252, 0.58)",
            "itemBorderRadius": 12
        },
        "Card": {
            "borderRadiusLG": 18
        },
        "Table": {
            "headerBg": "#f3f7fb",
            "headerColor": "#1c2430",
            "borderColor": "#d7e1ee"
        }
    }
}

const antdConfig: ConfigProviderProps = {
    theme: CrudDefaultTheme,
    locale: plPL
}

function Layout() {
    const auth = useAuth0();
    const roles = useCurrentUserRoles();
    const currentUser = useSelector(getCurrentUser);
    const location = useLocation();
    const [showSider, setShowSider] = useState<boolean>(window.innerWidth > 576);
    const [isQrCameraScannerEnabled, setIsQrCameraScannerEnabled] = useState(() => isQrCameraScannerEnabledForUser(currentUser));
    const apiService = getApiServiceOrNull();

    console.log(roles);

    useEffect(() => {
        const resize = () => {
            const shouldShowSider = window.innerWidth > 576;
            if (shouldShowSider !== showSider) setShowSider(shouldShowSider);
        }
        window.addEventListener('resize', resize);
        return () => window.removeEventListener('resize', resize);
    }, [showSider]);

    useEffect(() => {
        setIsQrCameraScannerEnabled(isQrCameraScannerEnabledForUser(currentUser));
    }, [currentUser]);

    useEffect(() => {
        const syncScannerPreference = () => {
            setIsQrCameraScannerEnabled(isQrCameraScannerEnabledForUser(currentUser));
        };

        window.addEventListener("storage", syncScannerPreference);
        window.addEventListener(QR_CAMERA_SCANNER_PREFERENCE_CHANGED_EVENT, syncScannerPreference);

        return () => {
            window.removeEventListener("storage", syncScannerPreference);
            window.removeEventListener(QR_CAMERA_SCANNER_PREFERENCE_CHANGED_EVENT, syncScannerPreference);
        };
    }, [currentUser]);

    const {scannerModalContextHolder, handleScanData} = useScannerListener({
        prefix: "SCAN:",
        suffix: ":SCAN",
        apiService
    });

    const isEmbeddedPipedrivePanel = location.pathname === RoutingPaths.pipedrivePanel.route;
    const isEmbeddedPipedriveOrderPreview = location.pathname === RoutingPaths.pipedriveOrderPreview.route;
    const isEmbeddedPipedriveSettings = location.pathname === RoutingPaths.pipedriveSettings.route;

    if (isEmbeddedPipedrivePanel || isEmbeddedPipedriveOrderPreview || isEmbeddedPipedriveSettings) {
        return <ConfigProvider {...antdConfig}>
            <ScrollToTopOnRouteChange/>
            <RoutingContainer/>
        </ConfigProvider>;
    }

    return <ConfigProvider {...antdConfig}>
        <ScrollToTopOnRouteChange/>
        <CrudLayout
            siderFullHeight={true}
            breadcrumb={<CrudBreadcrumb routingDefinitions={RoutingPaths}/>}
            siderMenu={<SiderMenu/>}
            header={<HeaderContainer/>}
            footerContent={<FooterContainer/>}
            showSider={auth.isAuthenticated && roles !== undefined && (roles?.length || 0) > 0 && showSider}
            configProviderProps={antdConfig}
            siderProps={{theme: "light"}}
        >
            <>
                <RouteContextBanner/>
                <RoutingContainer/>
                {auth.isAuthenticated && <QrCameraScannerFab isEnabled={isQrCameraScannerEnabled} onScan={handleScanData}/>}
                {scannerModalContextHolder}
                {process.env.NODE_ENV === "development" && <DevScannerSimulator apiService={apiService}/>}
            </>
        </CrudLayout>
    </ConfigProvider>
}

export default App;
